import {
  Cart,
  CartItem,
  CartSelectedBundle,
  GiftCardHolder,
  Product,
  ProductPromotionResult,
  ShopProfile,
} from "@lemmonhq/types";
import { reactive, ref, watch, WatchHandle } from "vue";
import {
  calculateAllowedOrderServiceMethods,
  generateCartItemId,
} from "./cartUtils";
import { getDeliveryCities } from "../utils";
import { debounce } from "@lemmonhq/orders";
import { nanoid } from "nanoid";

export type CartAPI = ReturnType<typeof useCart>;
export function useCart() {
  const cart = reactive<Cart>({
    items: [],
    checkoutIds: [],
    customers: [],
    serviceMethod: undefined,
    allowedOrderServiceMethods: undefined,
  });
  const activeCustomerId = ref<string | undefined>();
  const showFoodTypes = ref(false);
  const isAddSeatsModalOpen = ref(false);
  const isTableSwitcherOpen = ref(false);
  const activeSection = ref<"cart" | "table">("cart");
  //
  const addToCart = (
    product: Product,
    selectedBundles?: CartSelectedBundle[],
    customPrice?: number,
    quantity?: number,
    notes?: string,
    promotionResult?: ProductPromotionResult,
    options?: {
      tableCreditTableId?: number | null;
      giftCardHolder?: GiftCardHolder | null;
    },
  ) => {
    const tableCreditTableId = options?.tableCreditTableId ?? null;
    const giftCardHolder = options?.giftCardHolder ?? null;
    const holderKey = giftCardHolder
      ? [giftCardHolder.name, giftCardHolder.email, giftCardHolder.phone]
          .map((v) => v || "")
          .join("|")
      : "";
    const cartItemId = generateCartItemId(
      product.id,
      selectedBundles,
      customPrice,
      activeCustomerId.value,
      // Credit for different tables, and gift cards for different people,
      // must stay on separate lines.
      tableCreditTableId
        ? `table-credit:${tableCreditTableId}`
        : holderKey.replace(/\|/g, "")
          ? `gift-card-holder:${holderKey}`
          : undefined,
    );
    // For custom price items, we need to check both product ID and price
    const existingItemIndex = cart.items.findIndex(
      (item) => item.guid === cartItemId,
    );

    if (existingItemIndex >= 0) {
      cart.items[existingItemIndex].quantity += quantity || 1;
    } else {
      const newItem = createCartItem(product, {
        selectedBundles,
        customPrice,
        quantity,
        notes,
        promotionResult,
        customerId: activeCustomerId.value,
        cartItemId,
      });
      if (tableCreditTableId) newItem.tableCreditTableId = tableCreditTableId;
      if (giftCardHolder) newItem.giftCardHolder = giftCardHolder;
      cart.items.push(newItem);
    }
  };

  const incrementQuantity = (cartItemId: string) => {
    const item = cart.items.find((item) => item.guid === cartItemId);
    if (item) {
      item.quantity += 1;
    }
  };

  const decrementQuantity = (cartItemId: string) => {
    const index = cart.items.findIndex((item) => item.guid === cartItemId);
    if (index >= 0) {
      const item = cart.items[index];
      if (item.quantity > 1) {
        item.quantity -= 1;
      } else {
        cart.items.splice(index, 1);
      }
    }
  };

  const removeItem = (cartItemId: string) => {
    const index = cart.items.findIndex((item) => item.guid === cartItemId);
    if (index >= 0) {
      cart.items.splice(index, 1);
    }
  };

  const updateNotes = (notes: { [key: string]: string }) => {
    cart.items.forEach((item) => {
      const itemKey = item.guid;
      item.note = notes[itemKey];
    });
  };

  const updateCart = (newCart: Cart) => {
    const nextCustomers = newCart.customers || [];
    activeCustomerId.value = undefined;

    // Update cart properties while maintaining reactivity
    cart.items = newCart.items || [];
    cart.customers = nextCustomers;
    cart.tableId = newCart.tableId;
    cart.checkoutIds = newCart.checkoutIds;
    cart.checkoutState = newCart.checkoutState;
  };
  const reset = (attrs?: {
    checkoutIds?: string[];
    checkoutState?: Cart["checkoutState"];
  }) => {
    activeCustomerId.value = undefined;
    updateCart({
      items: [],
      customers: [],
      checkoutIds: attrs?.checkoutIds || [],
      checkoutState: attrs?.checkoutState || "ordering",
    });
    //
  };
  const getStorageKey = (shop: ShopProfile, key: string) =>
    "ls_shop_" + shop.id + "_" + key;

  // todo: warning: here be dragons
  // we need cartWatchHandle.stop() since enable function can be called multiple times
  let cartWatchHandle: WatchHandle | undefined;
  let deliveryFeeWatchHandle: WatchHandle | undefined;
  let currentShopKey: string | undefined;
  const localStorageSync = {
    enable: (shop: ShopProfile, key: string) => {
      const lsKey = getStorageKey(shop, key);
      console.log(`Loading cart from localStorage with key: ${lsKey}`);

      // 1) load from localstorage into `cart` reactive variable when called
      try {
        const storedCart = localStorage.getItem(lsKey);
        if (storedCart) {
          const parsedCart = JSON.parse(storedCart) as Cart;
          // Validate cart structure before applying
          if (parsedCart && Array.isArray(parsedCart.items)) {
            updateCart(parsedCart);
            console.log(
              `Loaded cart with ${parsedCart.items.length} items from localStorage`,
            );
          } else {
            console.warn("Invalid cart structure in localStorage, skipping");
          }
        } else {
          console.log("No cart found in localStorage");
        }
      } catch (error) {
        console.warn("Failed to load cart from localStorage:", error);
        // Clear corrupted data
        try {
          localStorage.removeItem(lsKey);
        } catch (clearError) {
          console.warn(
            "Failed to clear corrupted localStorage data:",
            clearError,
          );
        }
      }

      if (cartWatchHandle) {
        cartWatchHandle.stop();
      }

      // 2) setup a watch listener on `cart` reactive to flush it into localstorage with debouncing
      const debouncedSave = debounce((newCart: Cart) => {
        try {
          localStorage.setItem(lsKey, JSON.stringify(newCart));
          console.log(
            `Saved cart with ${newCart.items.length} items to localStorage`,
          );
        } catch (error) {
          console.warn("Failed to save cart to localStorage:", error);
        }
      }, 500);

      cartWatchHandle = watch(() => cart, debouncedSave, { deep: true });
    },
  };

  return {
    cart,
    addToCart,
    incrementQuantity,
    decrementQuantity,
    removeItem,
    updateNotes,
    updateCart,
    activeCustomerId,
    showFoodTypes,
    isAddSeatsModalOpen,
    isTableSwitcherOpen,
    activeSection,
    reset,
    calculateAllowedOrderServiceMethods,
    //allowedOrderServiceMethods,
    setupShop: async (shop: ShopProfile, key: string) => {
      const shopKey = `${shop.id}_${key}`;

      // Skip if already set up with same parameters
      if (currentShopKey === shopKey) {
        console.log(`Shop already set up for key: ${shopKey}, skipping`);
        return cart.allowedOrderServiceMethods;
      }

      console.log(`Setting up shop: ${shop.id} with key: ${key}`);
      console.log(
        `Current localStorage key will be: ${getStorageKey(shop, key)}`,
      );

      currentShopKey = shopKey;

      // Cleanup previous watches
      if (cartWatchHandle) {
        cartWatchHandle.stop();
        cartWatchHandle = undefined;
      }
      if (deliveryFeeWatchHandle) {
        deliveryFeeWatchHandle.stop();
        deliveryFeeWatchHandle = undefined;
      }

      const allowedMethods = calculateAllowedOrderServiceMethods(shop);
      cart.allowedOrderServiceMethods = allowedMethods;

      // Set default service method (prefer delivery over takeaway)
      if (!cart.serviceMethod) {
      }

      // Setup delivery fee calculation watch
      deliveryFeeWatchHandle = watch(
        () => cart.selectedAddress,
        (selectedAddress) => {
          if (cart.serviceMethod === "delivery" && selectedAddress) {
            const deliveryCities = getDeliveryCities(shop.settings || []);
            // Find the matching delivery city based on the selected address
            const matchingCity = deliveryCities.find(
              (city) =>
                city.name.toLowerCase() === selectedAddress.city.toLowerCase(),
            );
            if (matchingCity) {
              cart.deliveryFee = {
                label: `(${matchingCity.name})`,
                value: parseFloat(matchingCity.fee),
              };
            } else {
              // Address city doesn't match any delivery cities
              cart.deliveryFee = undefined;
            }
          } else {
            cart.deliveryFee = undefined;
          }
        },
        { immediate: true },
      );

      await localStorageSync.enable(shop, key);
      console.log(`Shop setup completed for: ${shopKey}`);
      return allowedMethods;
    },
  };
}

export const createCartItem = (
  product: Product,
  options: {
    selectedBundles?: CartSelectedBundle[];
    customPrice?: number;
    quantity?: number;
    notes?: string;
    promotionResult?: ProductPromotionResult;
    customerId?: string;
    cartItemId: string;
  },
): CartItem => {
  const basePrice =
    (options.customPrice ? options.customPrice.toFixed(2) : null) ||
    product.price;
  const bundlePrice =
    options.selectedBundles?.reduce(
      (total, bundle) =>
        total + bundle.options.reduce((sum, option) => sum + option.price, 0),
      0,
    ) || 0;

  return {
    guid: options.cartItemId,
    cartId: nanoid(),
    productId: product.id,
    name: product.name,
    price: options.promotionResult
      ? options.promotionResult.finalPrice.toFixed(2)
      : basePrice,
    fullPrice: options.promotionResult
      ? options.promotionResult.finalPrice
      : parseFloat(basePrice) + bundlePrice,
    maxRetailPrice: options.promotionResult
      ? options.promotionResult.originalPrice
      : parseFloat(basePrice),
    hasPromotions: options.promotionResult ? true : undefined,
    bundlePrice: bundlePrice.toFixed(2),
    quantity: options.quantity || 1,
    itemType: product.item_type,
    foodTypeId: product.food_type_id,
    selectedBundles: options.selectedBundles,
    type: product.type,
    isCustom: options.customPrice
      ? Number.isFinite(options.customPrice)
      : false,
    customerId: options.customerId,
    note: options.notes,
  };
};

export const calculateCartItemTotal = (item: CartItem): number => {
  const basePrice = parseFloat(item.price);
  const bundlePrice =
    item.selectedBundles?.reduce(
      (total, bundle) =>
        total + bundle.options.reduce((sum, option) => sum + option.price, 0),
      0,
    ) || 0;
  return (basePrice + bundlePrice) * item.quantity;
};

export const calculateCartSubtotal = (cart: Cart): number => {
  return cart.items.reduce(
    (sum, item) => sum + calculateCartItemTotal(item),
    0,
  );
};
